/**
 * Deposits owed back: bookings the patient (or the clinic) cancelled after the
 * deposit was paid. Cancelling records the amount (PUT /bookings/:id/status);
 * here the doctor transfers it back, attaches the transfer's receipt and
 * settles it (PUT /bookings/:id/refund), and the patient finds the receipt
 * under «المعاملات» — as the cancel dialog promised them.
 *
 * Shown only while something is owed, at the top of the overview: money held
 * for a visit that will not happen is the first thing to clear.
 */

import { useMemo, useState } from 'react';
import { Badge, Button, Icon, Modal, Panel, SectionTitle } from '../../components/common';
import { useAsync } from '../../hooks/useAsync';
import { useNotifications } from '../../context/NotificationContext';
import { bookingApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { refundState, typeLabel } from '../../domain/bookings';
import { formatTime12, isoToDisplayDate } from '../../domain/dates';
import { compressImage } from '../../domain/files';

export function RefundsOwed({ doctorId }) {
  const state = useAsync(() => bookingApi.list({ doctorId, status: 'cancelled' }), [doctorId], {
    enabled: Boolean(doctorId),
  });
  const owed = useMemo(
    () =>
      (state.data || [])
        .filter((b) => refundState(b) === 'owed')
        .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`)),
    [state.data],
  );
  const [target, setTarget] = useState(null);

  if (!owed.length) return null;
  const total = owed.reduce((sum, b) => sum + (Number(b.refundAmount) || 0), 0);

  return (
    <Panel className="border border-amber-500/30">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle icon="rotate-ccw" className="!mb-0">
          عرابين مستحقة الرد
        </SectionTitle>
        <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
          {owed.length} {owed.length === 1 ? 'حجز' : 'حجوزات'} — {formatCurrency(total)}
        </Badge>
      </div>
      <ul className="space-y-2">
        {owed.map((booking) => (
          <li
            key={booking.id}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"
          >
            <div className="min-w-0 flex-1 basis-40">
              <p className="truncate text-sm font-bold text-white">{booking.patientName}</p>
              <p className="text-[11px] text-slate-400">
                ألغى {typeLabel(booking.type)} {isoToDisplayDate(booking.date)} — {formatTime12(booking.time)}
              </p>
            </div>
            <span className="text-sm font-black text-amber-300">{formatCurrency(booking.refundAmount)}</span>
            <Button variant="success" icon="send" onClick={() => setTarget(booking)}>
              رد العربون
            </Button>
          </li>
        ))}
      </ul>
      {target && (
        <SettleRefund
          booking={target}
          onClose={() => setTarget(null)}
          onSettled={() => {
            setTarget(null);
            state.reload();
          }}
        />
      )}
    </Panel>
  );
}

function SettleRefund({ booking, onClose, onSettled }) {
  const { success, error: toastError } = useNotifications();
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setReceipt(await compressImage(file, { maxWidth: 1400, quality: 0.72 }));
    } catch (err) {
      toastError(err.message || 'تعذر قراءة الصورة');
    }
  };

  const settle = async () => {
    setBusy(true);
    try {
      await bookingApi.settleRefund(booking.id, receipt);
      success('تم رد العربون — يجده المريض في «المعاملات» بإيصاله');
      onSettled();
    } catch (err) {
      toastError(err.message || 'تعذر تسجيل رد العربون');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="رد العربون" icon="rotate-ccw" size="sm">
      <div className="space-y-4">
        <dl className="space-y-1.5 rounded-xl border border-white/5 bg-white/5 p-3 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-400">المريض</dt>
            <dd className="truncate font-bold text-white">{booking.patientName}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-400">الهاتف</dt>
            <dd className="font-bold text-white" dir="ltr">{booking.patientPhone || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-400">المبلغ المستحق</dt>
            <dd className="font-black text-amber-300">{formatCurrency(booking.refundAmount)}</dd>
          </div>
        </dl>

        {booking.depositReceipt && (
          <div>
            <Button variant="subtle" icon="eye" onClick={() => setShowDeposit((open) => !open)}>
              {showDeposit ? 'إخفاء إيصال العربون' : 'إيصال العربون الذي دفعه'}
            </Button>
            {showDeposit && (
              <img
                src={booking.depositReceipt}
                alt="إيصال العربون"
                className="mx-auto mt-2 max-h-56 rounded-lg object-contain"
              />
            )}
          </div>
        )}

        <label
          className={`flex min-h-[64px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed p-3 text-sm font-bold transition-all ${
            receipt
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
              : 'border-white/20 text-slate-400 hover:border-emerald-500/50 hover:text-emerald-400'
          }`}
        >
          <Icon name={receipt ? 'check-circle-2' : 'image-plus'} size={18} />
          {receipt ? 'إيصال تحويل الرد مرفق — اضغط للتغيير' : 'أرفق صورة إيصال تحويل الرد'}
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} className="hidden" />
        </label>
        {receipt && (
          <img src={receipt} alt="إيصال الرد" className="mx-auto max-h-56 rounded-lg object-contain" />
        )}

        <Button variant="success" icon="check" className="w-full" disabled={!receipt} loading={busy} onClick={settle}>
          تأكيد رد العربون
        </Button>
        <p className="text-center text-[11px] text-slate-500">
          يرى المريض الإيصال وحالة «تم الاسترداد» في صفحة المعاملات.
        </p>
      </div>
    </Modal>
  );
}

export default RefundsOwed;
